import classNames from 'classnames';

import { thousandSeparated } from 'shared/util/formatting';

import { type HotelOffer, KayakHotelProvider, type RoomWithPeople } from '@/interfaces';
import { Image } from '@/components/image/Image';
import { DirectBookingBanner } from './components/DirectBookingBanner';
import { mapHotelRoom, mapInclusions } from '../../../../utils';
import { BookingLoginBoundary } from '@/components/bookingLoginBoundary/BookingLoginBoundary';

export interface Props {
    offer: HotelOffer;
    rooms: readonly RoomWithPeople[];
    nights: number;
    useDirectBanner?: boolean;
    previousIsDirect?: boolean;
    nextIsDirect?: boolean;
    isPrimary?: boolean;
}

export const generateNightsText = (nights: number): string => `for ${nights === 1 ? '1 natt' : `${nights} netter`}`;

export const HotelOfferListItem = ({ offer, rooms, nights, useDirectBanner, previousIsDirect, nextIsDirect, isPrimary }: Props) => {
    if (!offer?.provider?.code) {
        return null;
    }
    const provider = offer.provider;
    const isRevato = provider?.code === KayakHotelProvider.REVATO;
    const directWithBanner = useDirectBanner && offer.isDirect;

    return (
        <div
            className={classNames('ads__unit', {
                'u-bg-ice': directWithBanner || isPrimary,
                'u-bg-milk': !(directWithBanner || isPrimary),
                'has-previous-sibling': previousIsDirect,
                'has-next-sibling': nextIsDirect,
            })}
            data-testid="offerDetails">
            <div className="u-size3of5--gt340 u-size1of2--lt340 flex flex-column--lt640 flex-justify-start">
                <div className="cro-hotel-offer-image u-mr16--gt640">
                    <div
                        className={classNames('image-container image-container-1x3 logo', {
                            'u-hide-lt480': isRevato && !directWithBanner,
                        })}
                        data-testid="offerDetailsProviderLogoOrName">
                        <Image image={provider.logo} altText={provider.name} frame={false} />
                    </div>
                </div>
                <div data-testid="offerDetailsRoomName">
                    {mapHotelRoom(offer.id, offer.rooms)}
                    <ul className="u-d1 u-pl16--lt640 u-pl32--gt640">
                        {mapInclusions(offer.id, offer.inclusions)}
                        {offer.hasFreeCancellation === true ? <li data-testid="offerFreeCancellation">Gratis avbestilling</li> : null}
                        {offer.canPayLater === true ? <li data-testid="offerCanPayLater">Betal senere</li> : null}
                    </ul>
                </div>
            </div>
            <div className="u-size2of5--gt340 u-size1of2--lt340 flex flex-column--lt768 flex-align-center flex-align-end--lt768">
                {directWithBanner && <DirectBookingBanner text="Book hos hotellet" width="125px" />}
                <div className="u-r-size1of2 u-text-right u-pr32--gt768 u-mb8--lt768">
                    <div className="u-t3 u-strong" data-testid="offerDetailsTotalRate">
                        {thousandSeparated(Math.round(offer.totalRate))} kr
                    </div>
                    <span className="u-d1--lt768" data-testid="offerDetailsNumberOfRoomsSearched">
                        {generateNightsText(nights)}
                    </span>
                </div>
                <div className="u-r-size1of2 u-text-right">
                    <BookingLoginBoundary
                        offer={offer}
                        rooms={rooms}
                        trackingPage="hotel-page"
                        buttonClass="button button--cta button--order-hotel"
                        buttonText={offer.integratedBooking ? 'Bestill nå' : 'Vis tilbud'}
                    />
                </div>
            </div>
        </div>
    );
};
